import type { ReactNode } from "react";
import { Row } from "@hightouch/htui";

import { useHorizontalScrollFade } from "src/hooks/use-horizontal-scroll-fade";
import { COMPOSER_TOOLBAR_SCROLLER_ATTR } from "src/components/llm/components/compact-chat-input-layout";
import { HIDDEN_SCROLLBAR_SX } from "src/components/llm/context/chat-gutter-context";

// Room for the pills' 3px focus ring and active shadow, which the scroller
// would otherwise clip; the negative margin keeps the controls where they were.
const RING_ROOM = "3px";

/**
 * The composer's conditional controls (pickers, toggles, chips) on one line.
 * When they outgrow the toolbar they scroll sideways behind a soft edge fade
 * instead of wrapping or pushing the trailing dictation/submit controls out of
 * the box. The caller anchors those trailing controls outside this scroller.
 */
export const ComposerToolbarScroller = ({
  children,
}: {
  children: ReactNode;
}) => {
  // One stable content wrapper, so the fade tracks the controls' width however
  // they mount and change inside it.
  const { ref, sx } = useHorizontalScrollFade({ observeContent: true });

  return (
    <Row
      ref={ref}
      {...{ [COMPOSER_TOOLBAR_SCROLLER_ATTR]: true }}
      minWidth={0}
      overflowX="auto"
      overflowY="hidden"
      p={RING_ROOM}
      m={`-${RING_ROOM}`}
      sx={{ ...sx, ...HIDDEN_SCROLLBAR_SX }}
    >
      {/* Natural width: the controls scroll rather than squish. */}
      <Row align="center" gap={2} flexShrink={0}>
        {children}
      </Row>
    </Row>
  );
};
